import { useEffect, useMemo, useState } from 'react'

import { Grid } from '@mantine/core'

import EmailAnalyticsHeroStrip from './EmailAnalyticsHeroStrip'
import EmailVolumeContainer from './EmailVolumeContainer'
import EmailActivityByEmployeeContainer from './EmailActivityByEmployeeContainer'
import EmailFiltersBar from './EmailFiltersBar'
import { FiltersPortal } from '../CallAnalyticsPage/components/FiltersPortal'
import {
  activityAnalyticsDateRangeNeedsUrlSync,
  getActivityAnalyticsUrlDateRange,
} from '../CallAnalyticsPage/activityAnalyticsDateRange'
import {
  serializeDateRangeForUrl,
  updateUrl,
} from '../CallAnalyticsPage/utils'

export interface EmailAnalyticsTabProps {
  /**
   * Bumped by the page-level Refresh button. Threaded through to
   * containers (e.g. EmailVolumeContainer) that own their own useQuery so
   * they can call refetch() without us re-keying the whole tab — which
   * would reset local state like the Email Volume bucket toggle.
   */
  refreshKey: number
}

const EmailAnalyticsTab = ({ refreshKey }: EmailAnalyticsTabProps) => {
  const [startDate, setStartDate] = useState(
    () => getActivityAnalyticsUrlDateRange().defaultStartDate
  )
  const [endDate, setEndDate] = useState(
    () => getActivityAnalyticsUrlDateRange().defaultEndDate
  )

  useEffect(() => {
    if (activityAnalyticsDateRangeNeedsUrlSync(startDate, endDate)) {
      updateUrl('dateRange', serializeDateRangeForUrl(startDate, endDate))
    }
  }, [startDate, endDate])

  const filters = useMemo(
    () => ({
      dateRange: { gte: startDate, lte: endDate },
    }),
    [startDate, endDate]
  )

  return (
    <>
      <FiltersPortal>
        <EmailFiltersBar
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
      </FiltersPortal>
      <EmailAnalyticsHeroStrip filters={filters} refreshKey={refreshKey} />
      <Grid gutter="xs">
        <Grid.Col span={12}>
          <EmailVolumeContainer filters={filters} refreshKey={refreshKey} />
        </Grid.Col>
        <Grid.Col span={12}>
          <EmailActivityByEmployeeContainer
            filters={filters}
            refreshKey={refreshKey}
          />
        </Grid.Col>
      </Grid>
    </>
  )
}

export default EmailAnalyticsTab
